import React, { useState, useRef } from "react";
import {
  Card,
  Input,
  Space,
  Button,
  message,
  Spin,
  Alert,
  Table,
  Divider,
  Typography,
  Collapse,
  Tag,
  Progress,
  Radio,
  Statistic,
  Row,
  Col,
} from "antd";
import {
  SearchOutlined,
  DownloadOutlined,
  CopyOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts/core";
import { GraphChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { EChartsOption } from "echarts";

// Import watermark function
import { addWatermarkToImage } from "../common/Watermark";

// 注册必需的组件
echarts.use([
  GraphChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  CanvasRenderer,
]);

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;

// API Configuration - 使用多个API key轮询
const MORALIS_API_KEYS = [
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJub25jZSI6IjI1MDk1ZmExLTcxYjAtNDFjZC05NTI2LTAwYWYxMzE5MmRlMiIsIm9yZ0lkIjoiNDU5NjA5IiwidXNlcklkIjoiNDcyODU0IiwidHlwZUlkIjoiNTZlN2ExYzgtYTkwNC00YjNhLWI1NzgtNzk0NjE3ZjdhOWY2IiwidHlwZSI6IlBST0pFQ1QiLCJpYXQiOjE3NTI2NTgzMDAsImV4cCI6NDkwODQxODMwMH0.jS2uJhm8qhv12o0Q4o8sSGIw1MphmzRhRvUADB85Mns",
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJub25jZSI6IjQ2M2QyM2EzLWNhMjEtNDNkZC05YTdmLTQ0ZjJkMTQwNGM3ZiIsIm9yZ0lkIjoiNDc4ODExIiwidXNlcklkIjoiNDkyNjA0IiwidHlwZUlkIjoiMWY0ZThhNjctZjVhYy00YzQ0LWE3ZTgtYmJhNzExNjU5NzEwIiwidHlwZSI6IlBST0pFQ1QiLCJpYXQiOjE3NjE5MTUxNjcsImV4cCI6NDkxNzY3NTE2N30.FzeXbUxRWmVm6zS2Tsr69u8wFRpPGtLqpTqGFB0N54g",
];

const ETHERSCAN_API_KEY = "MVPP4EJFKZ2D86FGNH21W6R385PK75A29Q";
const MIN_BNB_AMOUNT = 2; // Minimum BNB amount to filter transactions
const MAIN_TX_COUNT = 50; // Query last 50 transactions for each address
const REQUEST_DELAY = 300; // 减少延迟到300ms以提高效率
const FETCH_TIMEOUT = 15000; // Request timeout
const CONCURRENT_REQUESTS = 3; // 并发请求数量

// API key 轮询管理器
class APIKeyManager {
  private keys: string[];
  private currentIndex: number = 0;

  constructor(keys: string[]) {
    this.keys = keys;
  }

  getNextKey(): string {
    const key = this.keys[this.currentIndex];
    this.currentIndex = (this.currentIndex + 1) % this.keys.length;
    return key;
  }

  getCurrentIndex(): number {
    return this.currentIndex;
  }
}

const moralisKeyManager = new APIKeyManager(MORALIS_API_KEYS);

// API Provider types
type APIProvider = "moralis" | "etherscan";

// Types
interface Transaction {
  hash: string;
  from: string;
  to: string;
  value: string;
  timeStamp: string;
  input: string;
  blockNumber: string;
}

interface WalletFlowRecord {
  walletAddress: string;
  transactions: Transaction[];
  toCollectionTx?: Transaction; // Direct transaction to collection address
  relayPath?: {
    relayAddress: string;
    toRelayTx: Transaction;
    toCollectionTx: Transaction;
  };
  status: "success" | "failed" | "no_flow";
  error?: string;
}

interface BatchAnalysisResult {
  collectionAddress: string;
  walletFlows: WalletFlowRecord[];
  analysisTime: string;
  totalWallets: number;
  successCount: number;
  failedCount: number;
  noFlowCount: number;
}

// Utility functions
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchWithTimeout = async (
  url: string,
  options?: RequestInit,
  timeout: number = FETCH_TIMEOUT
) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError") {
      throw new Error("Request timeout");
    }
    throw error;
  }
};

// Moralis API: Get address transactions - 使用轮询的API key
const getTransactionsFromMoralis = async (
  address: string,
  limit: number = MAIN_TX_COUNT
): Promise<Transaction[]> => {
  const url = `https://deep-index.moralis.io/api/v2/${address}?chain=bsc&limit=${limit}`;
  const apiKey = moralisKeyManager.getNextKey(); // 轮询获取API key

  const response = await fetchWithTimeout(url, {
    headers: {
      "X-API-Key": apiKey,
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Moralis API error: ${response.status}`);
  }

  const data = await response.json();

  if (!data.result || !Array.isArray(data.result)) {
    throw new Error("Moralis API returned invalid data");
  }

  // Normalize Moralis format to match Etherscan format
  return data.result.map((tx: any) => ({
    hash: tx.hash || tx.transaction_hash || "",
    from: tx.from_address || tx.from || "",
    to: tx.to_address || tx.to || "",
    value: tx.value || "0",
    timeStamp: tx.block_timestamp
      ? String(Math.floor(new Date(tx.block_timestamp).getTime() / 1000))
      : "0",
    input: tx.input || tx.data || "0x",
    blockNumber: tx.block_number ? String(tx.block_number) : "0",
  }));
};

// Etherscan API: Get address transactions
const getTransactionsFromEtherscan = async (
  address: string,
  limit: number = MAIN_TX_COUNT
): Promise<Transaction[]> => {
  const url = `https://api.etherscan.io/v2/api?chainid=56&module=account&action=txlist&address=${address}&startblock=0&endblock=99999999&page=1&offset=${limit}&sort=desc&apikey=${ETHERSCAN_API_KEY}`;

  const response = await fetchWithTimeout(url);

  if (!response.ok) {
    throw new Error(`Etherscan API error: ${response.status}`);
  }

  const data = await response.json();

  if (data.status === "0" && data.message) {
    if (
      data.message.includes("rate limit") ||
      data.message.includes("Max rate") ||
      data.message.includes("temporarily unavailable")
    ) {
      throw new Error("Etherscan API rate limit or temporarily unavailable");
    }
  }

  if (!data.result || !Array.isArray(data.result)) {
    throw new Error("Etherscan API returned invalid data");
  }

  return data.result;
};

// Universal function to get transactions with API fallback
const getTransactions = async (
  address: string,
  limit: number = MAIN_TX_COUNT,
  preferredProvider: APIProvider = "moralis"
): Promise<Transaction[]> => {
  // Try preferred API first
  try {
    if (preferredProvider === "moralis") {
      return await getTransactionsFromMoralis(address, limit);
    } else {
      return await getTransactionsFromEtherscan(address, limit);
    }
  } catch (error: any) {
    console.warn(`${preferredProvider} API failed:`, error.message);

    // Fallback to alternative API
    const fallbackProvider: APIProvider =
      preferredProvider === "moralis" ? "etherscan" : "moralis";

    try {
      console.log(`Trying fallback API: ${fallbackProvider}`);
      if (fallbackProvider === "moralis") {
        return await getTransactionsFromMoralis(address, limit);
      } else {
        return await getTransactionsFromEtherscan(address, limit);
      }
    } catch (fallbackError: any) {
      console.error(
        `Both APIs failed. ${preferredProvider}: ${error.message}, ${fallbackProvider}: ${fallbackError.message}`
      );
      throw new Error(
        `All APIs failed: ${error.message}, ${fallbackError.message}`
      );
    }
  }
};

// Convert Wei to BNB
const weiToBNB = (wei: string): number => {
  return parseFloat(wei) / 1e18;
};

// Format BNB amount
const formatBNB = (amount: number): string => {
  return amount.toFixed(4);
};

// Shorten address for display
const shortenAddress = (address: string): string => {
  if (address.length <= 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const BatchFundFlowTracker: React.FC = () => {
  const [inputWallets, setInputWallets] = useState("");
  const [collectionAddress, setCollectionAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<BatchAnalysisResult | null>(null);
  const [apiProvider, setApiProvider] = useState<APIProvider>("moralis");
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const chartRef = useRef<ReactECharts>(null);

  // Analyze single wallet's fund flow to collection address
  const analyzeWalletFlow = async (
    walletAddress: string,
    collectionAddr: string,
    preferredProvider: APIProvider
  ): Promise<WalletFlowRecord> => {
    try {
      const transactions = await getTransactions(
        walletAddress,
        MAIN_TX_COUNT,
        preferredProvider
      );

      if (transactions.length === 0) {
        return {
          walletAddress,
          transactions: [],
          status: "no_flow",
        };
      }

      // Filter transactions with amount > MIN_BNB_AMOUNT
      const filteredTransactions = transactions.filter((tx) => {
        const amount = weiToBNB(tx.value);
        return amount >= MIN_BNB_AMOUNT;
      });

      // Sort by timestamp descending (newest first)
      const sortedTxs = [...filteredTransactions].sort(
        (a, b) => parseInt(b.timeStamp) - parseInt(a.timeStamp)
      );

      // Find direct transaction to collection address (from wallet)
      const directToCollection = sortedTxs.find(
        (tx) =>
          tx.from.toLowerCase() === walletAddress.toLowerCase() &&
          tx.to.toLowerCase() === collectionAddr.toLowerCase()
      );

      if (directToCollection) {
        return {
          walletAddress,
          transactions: sortedTxs,
          toCollectionTx: directToCollection,
          status: "success",
        };
      }

      // Find potential relay path: wallet -> relay -> collection
      // Step 1: Find all outflow transactions from wallet
      const outflowTxs = sortedTxs.filter(
        (tx) => tx.from.toLowerCase() === walletAddress.toLowerCase()
      );

      // Step 2: Check each outflow destination to see if it sends to collection
      for (const outflowTx of outflowTxs) {
        const relayAddress = outflowTx.to;

        // Query relay address transactions (无需延迟，因为批次已经有延迟且使用API key轮询)
        const relayTxs = await getTransactions(
          relayAddress,
          MAIN_TX_COUNT,
          preferredProvider
        );

        // Find transaction from relay to collection
        const relayToCollection = relayTxs.find(
          (tx) =>
            tx.from.toLowerCase() === relayAddress.toLowerCase() &&
            tx.to.toLowerCase() === collectionAddr.toLowerCase() &&
            weiToBNB(tx.value) >= MIN_BNB_AMOUNT
        );

        if (relayToCollection) {
          return {
            walletAddress,
            transactions: sortedTxs,
            relayPath: {
              relayAddress,
              toRelayTx: outflowTx,
              toCollectionTx: relayToCollection,
            },
            status: "success",
          };
        }
      }

      // No flow to collection address found
      return {
        walletAddress,
        transactions: sortedTxs,
        status: "no_flow",
      };
    } catch (error: any) {
      console.error(`Failed to analyze wallet ${walletAddress}:`, error);
      return {
        walletAddress,
        transactions: [],
        status: "failed",
        error: error.message,
      };
    }
  };

  const analyzeBatch = async () => {
    const wallets = inputWallets
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const collection = collectionAddress.trim();

    if (wallets.length === 0) {
      message.error("请输入至少一个钱包地址");
      return;
    }

    if (!/^0x[a-fA-F0-9]{40}$/.test(collection)) {
      message.error("请输入有效的归集地址");
      return;
    }

    // Validate wallet addresses
    const invalidWallets = wallets.filter(
      (addr) => !/^0x[a-fA-F0-9]{40}$/.test(addr)
    );
    if (invalidWallets.length > 0) {
      message.error(
        `以下地址格式不正确: ${invalidWallets.slice(0, 3).join(", ")}${
          invalidWallets.length > 3 ? "..." : ""
        }`
      );
      return;
    }

    setLoading(true);
    setResult(null);
    setProgress({ current: 0, total: wallets.length });

    try {
      const walletFlows: WalletFlowRecord[] = [];

      // 并发批次处理，每批处理 CONCURRENT_REQUESTS 个钱包
      for (let i = 0; i < wallets.length; i += CONCURRENT_REQUESTS) {
        const batch = wallets.slice(i, i + CONCURRENT_REQUESTS);

        message.info(
          `正在分析第 ${i + 1}-${Math.min(i + CONCURRENT_REQUESTS, wallets.length)} 个钱包 (共${wallets.length}个)`
        );

        // 并发处理当前批次
        const batchPromises = batch.map((wallet) =>
          analyzeWalletFlow(wallet, collection, apiProvider)
        );

        const batchResults = await Promise.all(batchPromises);
        walletFlows.push(...batchResults);

        setProgress({ current: Math.min(i + CONCURRENT_REQUESTS, wallets.length), total: wallets.length });

        // 批次之间添加延迟，避免API限流
        if (i + CONCURRENT_REQUESTS < wallets.length) {
          await delay(REQUEST_DELAY);
        }
      }

      const successCount = walletFlows.filter(
        (f) => f.status === "success"
      ).length;
      const failedCount = walletFlows.filter(
        (f) => f.status === "failed"
      ).length;
      const noFlowCount = walletFlows.filter(
        (f) => f.status === "no_flow"
      ).length;

      const batchResult: BatchAnalysisResult = {
        collectionAddress: collection,
        walletFlows,
        analysisTime: new Date().toLocaleString(),
        totalWallets: wallets.length,
        successCount,
        failedCount,
        noFlowCount,
      };

      setResult(batchResult);
      message.success(
        `分析完成！成功: ${successCount}, 无资金流: ${noFlowCount}, 失败: ${failedCount}`
      );
    } catch (error: any) {
      console.error("Batch analysis failed:", error);
      message.error(`批量分析失败: ${error.message}`);
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  // Copy all flow records to clipboard
  const copyAllFlowRecords = () => {
    if (!result) {
      message.warning("暂无数据");
      return;
    }

    const lines: string[] = [];

    result.walletFlows.forEach((flow) => {
      if (flow.status !== "success") return;

      if (flow.toCollectionTx) {
        // Direct flow
        const amt = formatBNB(weiToBNB(flow.toCollectionTx.value));
        lines.push(
          `${flow.toCollectionTx.from} -> ${flow.toCollectionTx.to} ${amt}BNB ${flow.toCollectionTx.hash}`
        );
      } else if (flow.relayPath) {
        // Relay flow
        const amt1 = formatBNB(weiToBNB(flow.relayPath.toRelayTx.value));
        lines.push(
          `${flow.relayPath.toRelayTx.from} -> ${flow.relayPath.toRelayTx.to} ${amt1}BNB ${flow.relayPath.toRelayTx.hash}`
        );

        const amt2 = formatBNB(weiToBNB(flow.relayPath.toCollectionTx.value));
        lines.push(
          `${flow.relayPath.toCollectionTx.from} -> ${flow.relayPath.toCollectionTx.to} ${amt2}BNB ${flow.relayPath.toCollectionTx.hash}`
        );
      }
    });

    if (lines.length === 0) {
      message.warning("没有可复制的资金流记录");
      return;
    }

    navigator.clipboard
      .writeText(lines.join("\n"))
      .then(() => message.success(`已复制 ${lines.length} 条资金流记录`))
      .catch(() => message.error("复制失败"));
  };

  // Export CSV
  const exportCSV = () => {
    if (!result) {
      message.warning("暂无数据");
      return;
    }

    const rows: string[] = [];
    rows.push("钱包地址,流向类型,中转地址,金额(BNB),交易Hash,状态");

    result.walletFlows.forEach((flow) => {
      if (flow.status === "success" && flow.toCollectionTx) {
        // Direct flow
        const amt = formatBNB(weiToBNB(flow.toCollectionTx.value));
        rows.push(
          `${flow.walletAddress},直接,无,${amt},${flow.toCollectionTx.hash},成功`
        );
      } else if (flow.status === "success" && flow.relayPath) {
        // Relay flow
        const amt = formatBNB(
          weiToBNB(flow.relayPath.toRelayTx.value) +
            weiToBNB(flow.relayPath.toCollectionTx.value)
        );
        rows.push(
          `${flow.walletAddress},中转,${flow.relayPath.relayAddress},${amt},"${flow.relayPath.toRelayTx.hash}, ${flow.relayPath.toCollectionTx.hash}",成功`
        );
      } else {
        rows.push(`${flow.walletAddress},-,-,-,-,${flow.status === "failed" ? "失败" : "无资金流"}`);
      }
    });

    const csv = rows.join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `batch_fund_flow_${new Date().getTime()}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    message.success("CSV导出成功");
  };

  // Generate visualization
  const getChartOption = (): EChartsOption => {
    if (!result) return {};

    const nodes: any[] = [];
    const links: any[] = [];

    // 布局配置
    const ySpacing = 100; // 节点间距
    const columnSpacing = 350; // 列间距
    const leftColumnX = 150; // 左列X坐标
    const middleColumnX = leftColumnX + columnSpacing; // 中间列X坐标（中转地址左列）
    const rightColumnX = middleColumnX + columnSpacing; // 右列X坐标（中转地址右列）
    const collectionX = rightColumnX + columnSpacing; // 归集地址X坐标
    const startY = 100; // 起始Y坐标

    // 先过滤出成功的流
    const successFlows = result.walletFlows.filter((f) => f.status === "success");

    // 计算动态画布高度
    const nodesPerColumn = Math.ceil(successFlows.length / 2);
    const calculatedHeight = Math.max(800, startY * 2 + nodesPerColumn * ySpacing);

    // 归集地址Y坐标（居中）
    const collectionY = calculatedHeight / 2;

    // Add collection node (target) - 动态位置
    nodes.push({
      id: result.collectionAddress,
      name: shortenAddress(result.collectionAddress),
      symbolSize: 60,
      x: collectionX,
      y: collectionY,
      itemStyle: {
        color: "#52c41a",
        borderColor: "#fff",
        borderWidth: 3,
      },
      label: {
        show: true,
        fontSize: 14,
        fontWeight: "bold",
        color: "#333",
      },
      tooltip: {
        formatter: () => {
          return `<strong>归集地址</strong><br/>地址: ${result.collectionAddress}<br/>流入钱包数: ${result.successCount}`;
        },
      },
      fixed: true,
    });

    // 用于追踪中转地址的位置索引
    const relayPositionMap = new Map<string, { x: number; y: number; columnIndex: number }>();
    let leftRelayCount = 0;
    let rightRelayCount = 0;

    // Add wallet nodes and links - 分为两列
    successFlows.forEach((flow, index) => {
      // 确定是左列还是右列
      const isLeftColumn = index % 2 === 0;
      const columnIndex = Math.floor(index / 2);

      // 计算钱包节点位置
      const walletX = isLeftColumn ? leftColumnX : leftColumnX + 200; // 钱包地址左右列间距较小
      const walletY = startY + columnIndex * ySpacing;

      // Add wallet node (source)
      nodes.push({
        id: flow.walletAddress,
        name: shortenAddress(flow.walletAddress),
        symbolSize: 45,
        x: walletX,
        y: walletY,
        itemStyle: {
          color: "#faad14", // 币安钱包用黄色
          borderColor: "#fff",
          borderWidth: 2,
        },
        label: {
          show: true,
          fontSize: 12,
          color: "#333",
        },
        tooltip: {
          formatter: () => {
            return `<strong>币安Web3钱包</strong><br/>地址: ${flow.walletAddress}<br/>交易数: ${flow.transactions.length}`;
          },
        },
        fixed: true,
      });

      if (flow.toCollectionTx) {
        // Direct link
        const amt = formatBNB(weiToBNB(flow.toCollectionTx.value));
        links.push({
          source: flow.walletAddress,
          target: result.collectionAddress,
          label: {
            show: true,
            formatter: `${amt} BNB`,
            fontSize: 10,
            color: "#ff4d4f",
            fontWeight: "bold",
          },
          lineStyle: {
            color: "#faad14", // 从钱包到归集用黄色
            width: 2,
            curveness: 0.2,
          },
          tooltip: {
            formatter: () => {
              return `<strong>直接转账</strong><br/>金额: ${amt} BNB<br/>Hash: ${flow.toCollectionTx!.hash}`;
            },
          },
        });
      } else if (flow.relayPath) {
        // Add relay node - 中转地址也分两列
        const relayId = `relay_${flow.relayPath.relayAddress}`;

        // Check if relay node already exists
        let relayPosition = relayPositionMap.get(relayId);

        if (!relayPosition) {
          // 决定中转地址放在左列还是右列
          const relayIsLeftColumn = (leftRelayCount <= rightRelayCount);
          const relayColumnIndex = relayIsLeftColumn ? leftRelayCount : rightRelayCount;

          const relayX = relayIsLeftColumn ? middleColumnX : rightColumnX;
          const relayY = startY + relayColumnIndex * ySpacing;

          relayPosition = { x: relayX, y: relayY, columnIndex: relayColumnIndex };
          relayPositionMap.set(relayId, relayPosition);

          // 更新计数
          if (relayIsLeftColumn) {
            leftRelayCount++;
          } else {
            rightRelayCount++;
          }

          nodes.push({
            id: relayId,
            name: shortenAddress(flow.relayPath.relayAddress),
            symbolSize: 35,
            x: relayX,
            y: relayY,
            itemStyle: {
              color: "#1890ff", // 中转地址用蓝色
              borderColor: "#fff",
              borderWidth: 2,
            },
            label: {
              show: true,
              fontSize: 10,
              color: "#333",
            },
            tooltip: {
              formatter: () => {
                return `<strong>中转地址</strong><br/>地址: ${flow.relayPath!.relayAddress}`;
              },
            },
            fixed: true,
          });
        }

        // Wallet -> Relay
        const amt1 = formatBNB(weiToBNB(flow.relayPath.toRelayTx.value));
        links.push({
          source: flow.walletAddress,
          target: relayId,
          label: {
            show: true,
            formatter: `${amt1} BNB`,
            fontSize: 9,
            color: "#ff4d4f",
          },
          lineStyle: {
            color: "#faad14", // 从钱包到中转用黄色
            width: 1.5,
            curveness: 0.2,
          },
          tooltip: {
            formatter: () => {
              return `<strong>转至中转</strong><br/>金额: ${amt1} BNB<br/>Hash: ${flow.relayPath!.toRelayTx.hash}`;
            },
          },
        });

        // Relay -> Collection
        const amt2 = formatBNB(weiToBNB(flow.relayPath.toCollectionTx.value));
        links.push({
          source: relayId,
          target: result.collectionAddress,
          label: {
            show: true,
            formatter: `${amt2} BNB`,
            fontSize: 9,
            color: "#ff4d4f",
          },
          lineStyle: {
            color: "#1890ff", // 从中转到归集用蓝色
            width: 1.5,
            curveness: 0.2,
          },
          tooltip: {
            formatter: () => {
              return `<strong>中转到归集</strong><br/>金额: ${amt2} BNB<br/>Hash: ${flow.relayPath!.toCollectionTx.hash}`;
            },
          },
        });
      }
    });

    return {
      title: {
        text: "批量资金流动网络图",
        subtext: `币安Web3钱包（双列） → 中转地址（双列） → 归集地址 | 成功追踪: ${result.successCount}/${result.totalWallets}`,
        left: "center",
        top: 10,
      },
      tooltip: {
        trigger: "item",
      },
      legend: [
        {
          data: [
            { name: "币安Web3钱包", itemStyle: { color: "#faad14" } },
            { name: "中转地址", itemStyle: { color: "#1890ff" } },
            { name: "归集地址", itemStyle: { color: "#52c41a" } },
          ],
          orient: "vertical",
          left: "left",
          top: 70,
        },
      ],
      series: [
        {
          type: "graph",
          layout: "none",
          data: nodes,
          links: links,
          roam: true,
          draggable: false,
          label: {
            show: true,
            position: "right",
          },
          emphasis: {
            focus: "adjacency",
            lineStyle: {
              width: 3,
            },
          },
          edgeSymbol: ["none", "arrow"],
          edgeSymbolSize: [0, 8],
          lineStyle: {
            opacity: 0.9,
            curveness: 0.2,
          },
        },
      ],
    };
  };

  // Export chart as image
  const handleExportChart = async () => {
    if (!chartRef.current) {
      message.error("图表未生成");
      return;
    }

    try {
      const chartInstance = chartRef.current.getEchartsInstance();
      const url = chartInstance.getDataURL({
        pixelRatio: 2,
        backgroundColor: "#fff",
        type: "png",
      });

      // Add watermark to the image
      const watermarkedUrl = await addWatermarkToImage(url, "0xluoye", 20, 120, 0.15);

      const link = document.createElement("a");
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, -5);
      link.download = `batch-fund-flow-${timestamp}.png`;
      link.href = watermarkedUrl;
      link.click();

      message.success("图片导出成功");
    } catch (error) {
      console.error("Export error:", error);
      message.error("导出失败");
    }
  };

  // Render detailed table
  const renderDetailedTable = () => {
    if (!result) return null;

    const dataSource: any[] = [];

    result.walletFlows.forEach((flow) => {
      if (flow.status === "success" && flow.toCollectionTx) {
        dataSource.push({
          key: `${flow.walletAddress}_direct`,
          wallet: flow.walletAddress,
          type: "直接",
          relay: "-",
          from: flow.toCollectionTx.from,
          to: flow.toCollectionTx.to,
          amount: formatBNB(weiToBNB(flow.toCollectionTx.value)),
          hash: flow.toCollectionTx.hash,
          time: new Date(
            parseInt(flow.toCollectionTx.timeStamp) * 1000
          ).toLocaleString(),
        });
      } else if (flow.status === "success" && flow.relayPath) {
        // Wallet -> Relay
        dataSource.push({
          key: `${flow.walletAddress}_relay_1`,
          wallet: flow.walletAddress,
          type: "中转(1/2)",
          relay: flow.relayPath.relayAddress,
          from: flow.relayPath.toRelayTx.from,
          to: flow.relayPath.toRelayTx.to,
          amount: formatBNB(weiToBNB(flow.relayPath.toRelayTx.value)),
          hash: flow.relayPath.toRelayTx.hash,
          time: new Date(
            parseInt(flow.relayPath.toRelayTx.timeStamp) * 1000
          ).toLocaleString(),
        });

        // Relay -> Collection
        dataSource.push({
          key: `${flow.walletAddress}_relay_2`,
          wallet: flow.walletAddress,
          type: "中转(2/2)",
          relay: flow.relayPath.relayAddress,
          from: flow.relayPath.toCollectionTx.from,
          to: flow.relayPath.toCollectionTx.to,
          amount: formatBNB(weiToBNB(flow.relayPath.toCollectionTx.value)),
          hash: flow.relayPath.toCollectionTx.hash,
          time: new Date(
            parseInt(flow.relayPath.toCollectionTx.timeStamp) * 1000
          ).toLocaleString(),
        });
      } else {
        dataSource.push({
          key: `${flow.walletAddress}_failed`,
          wallet: flow.walletAddress,
          type: flow.status === "failed" ? "失败" : "无资金流",
          relay: "-",
          from: "-",
          to: "-",
          amount: "-",
          hash: "-",
          time: "-",
        });
      }
    });

    const columns = [
      {
        title: "涉及的币安web3钱包",
        dataIndex: "wallet",
        key: "wallet",
        ellipsis: true,
        width: 150,
        render: (addr: string) => (
          <a
            href={`https://bscscan.com/address/${addr}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {shortenAddress(addr)}
          </a>
        ),
      },
      {
        title: "类型",
        dataIndex: "type",
        key: "type",
        width: 100,
        render: (type: string) => {
          let color = "blue";
          if (type.includes("中转")) color = "orange";
          if (type === "失败") color = "red";
          if (type === "无资金流") color = "gray";
          return <Tag color={color}>{type}</Tag>;
        },
      },
      {
        title: "中转地址",
        dataIndex: "relay",
        key: "relay",
        ellipsis: true,
        width: 120,
        render: (addr: string) =>
          addr !== "-" ? (
            <a
              href={`https://bscscan.com/address/${addr}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {shortenAddress(addr)}
            </a>
          ) : (
            "-"
          ),
      },
      {
        title: "From",
        dataIndex: "from",
        key: "from",
        ellipsis: true,
        width: 120,
        render: (addr: string) =>
          addr !== "-" ? shortenAddress(addr) : "-",
      },
      {
        title: "To",
        dataIndex: "to",
        key: "to",
        ellipsis: true,
        width: 120,
        render: (addr: string) => {
          if (addr === "-") return "-";
          const isCollection = addr.toLowerCase() === result.collectionAddress.toLowerCase();
          return (
            <span style={{ fontWeight: isCollection ? "bold" : "normal" }}>
              {shortenAddress(addr)}
            </span>
          );
        },
      },
      {
        title: "金额(BNB)",
        dataIndex: "amount",
        key: "amount",
        width: 100,
      },
      {
        title: "交易Hash",
        dataIndex: "hash",
        key: "hash",
        ellipsis: true,
        width: 150,
        render: (hash: string) =>
          hash !== "-" ? (
            <a
              href={`https://bscscan.com/tx/${hash}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {hash.slice(0, 10)}...{hash.slice(-8)}
            </a>
          ) : (
            "-"
          ),
      },
      {
        title: "时间",
        dataIndex: "time",
        key: "time",
        width: 160,
      },
    ];

    return (
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={{
          pageSize: 200,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        scroll={{ x: true }}
        size="small"
      />
    );
  };

  return (
    <Card
      title={
        <Space>
          <SearchOutlined />
          批量资金流动追踪工具（增强版）
        </Space>
      }
      extra={
        <Button
          type="link"
          icon={<InfoCircleOutlined />}
          href="https://bscscan.com"
          target="_blank"
        >
          BSCScan
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <Alert
          message="关于此工具"
          description="批量追踪多个币安Web3钱包的资金流动路径，自动识别直接转账和中转路径，最终汇入归集地址。支持可视化展示和数据导出。⚡ 已优化：使用多API key轮询 + 并发请求，查询速度提升3倍！"
          type="info"
          showIcon
          closable
        />

        {/* API Provider Selection */}
        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>API Provider:</div>
          <Radio.Group
            value={apiProvider}
            onChange={(e) => setApiProvider(e.target.value)}
            disabled={loading}
          >
            <Radio.Button value="moralis">Moralis (推荐)</Radio.Button>
            <Radio.Button value="etherscan">Etherscan (备用)</Radio.Button>
          </Radio.Group>
        </div>

        {/* Input Section */}
        <div>
          <Text strong>归集地址（资金最终汇入地址）:</Text>
          <Input
            placeholder="0x..."
            value={collectionAddress}
            onChange={(e) => setCollectionAddress(e.target.value)}
            size="large"
            style={{ marginTop: 8 }}
          />
        </div>

        <div>
          <Text strong>币安Web3钱包地址列表（每行一个）:</Text>
          <TextArea
            placeholder="0x...&#10;0x...&#10;0x..."
            value={inputWallets}
            onChange={(e) => setInputWallets(e.target.value)}
            rows={8}
            style={{ marginTop: 8, fontFamily: "monospace" }}
          />
        </div>

        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={analyzeBatch}
          loading={loading}
          size="large"
          block
        >
          {loading ? "分析中..." : "开始批量分析"}
        </Button>

        {loading && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <Spin size="large" tip={`分析进度: ${progress.current}/${progress.total}`} />
            <Progress
              percent={Math.round((progress.current / progress.total) * 100)}
              status="active"
              style={{ marginTop: 16 }}
            />
          </div>
        )}

        {/* Results Section */}
        {!loading && result && (
          <>
            <Divider>分析结果</Divider>

            {/* Statistics */}
            <Row gutter={16}>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="总钱包数"
                    value={result.totalWallets}
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="成功追踪"
                    value={result.successCount}
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="无资金流"
                    value={result.noFlowCount}
                    valueStyle={{ color: "#8c8c8c" }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="查询失败"
                    value={result.failedCount}
                    valueStyle={{ color: "#ff4d4f" }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Action Buttons */}
            <Space wrap>
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={copyAllFlowRecords}
              >
                一键复制全部资金流
              </Button>
              <Button icon={<DownloadOutlined />} onClick={exportCSV}>
                导出CSV
              </Button>
              {result.successCount > 0 && (
                <Button icon={<DownloadOutlined />} onClick={handleExportChart}>
                  导出可视化图
                </Button>
              )}
            </Space>

            {/* Visualization */}
            {result.successCount > 0 && (
              <Card title="资金流动可视化" size="small">
                <div
                  style={{
                    border: "1px solid #d9d9d9",
                    borderRadius: 8,
                    padding: 16,
                    minHeight: 600,
                    backgroundColor: "#fafafa",
                    overflow: "auto",
                  }}
                >
                  <ReactECharts
                    ref={chartRef}
                    echarts={echarts}
                    option={getChartOption()}
                    style={{
                      height: `${Math.max(
                        800,
                        200 + Math.ceil(result.successCount / 2) * 100
                      )}px`,
                      minWidth: "100%",
                    }}
                    opts={{ renderer: "canvas" }}
                  />
                </div>
              </Card>
            )}

            {/* Detailed Table */}
            <Card title="详细交易记录" size="small">
              {renderDetailedTable()}
            </Card>

            {/* Collapse for Usage Instructions */}
            <Collapse>
              <Panel header="使用说明" key="1">
                <Paragraph>
                  <Text strong>Step 1:</Text> 输入归集地址（资金最终汇入的地址）
                </Paragraph>
                <Paragraph>
                  <Text strong>Step 2:</Text> 输入币安Web3钱包地址列表（每行一个）
                </Paragraph>
                <Paragraph>
                  <Text strong>Step 3:</Text> 点击"开始批量分析"按钮
                </Paragraph>
                <Paragraph>
                  <Text strong>Step 4:</Text> 等待分析完成，查看结果
                </Paragraph>
                <Paragraph>
                  <Text strong>功能说明:</Text>
                  <ul>
                    <li>自动识别直接转账路径（钱包 → 归集地址）</li>
                    <li>自动识别中转路径（钱包 → 中转地址 → 归集地址）</li>
                    <li>支持批量查询，每个钱包查询最近50笔交易</li>
                    <li>
                      只显示金额 ≥ {MIN_BNB_AMOUNT} BNB 的交易记录
                    </li>
                    <li>提供可视化图表展示整体资金流动网络</li>
                    <li>支持一键复制所有资金流记录和CSV导出</li>
                  </ul>
                </Paragraph>
              </Panel>
            </Collapse>
          </>
        )}
      </Space>
    </Card>
  );
};

export default BatchFundFlowTracker;
